import type { WallDescriptor, WallSection } from "building-types/walls/wallDesciptorTypes";
import { WallCladdingType, WallSideCutType } from "building-types/walls/wallDesciptorTypes";
import type { WallSideGeneratedDatums } from "building-types/walls/wallSideGeneratedDatums";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";

/**
 * @deprecated should use a domain based approach since these values can overlap
 * @NOTE - do not export this function, it is only used internally
 *
 * @param datums - The wall side datums to determine the cut type for.
 * @param cutPlane - The plane to determine the cut type for.
 * @returns The cut type for the given wall side at the given plane.
 * @returns
 */
function wallSideCutType(datums: WallSideGeneratedDatums, cutPlane: LevelInches): WallSideCutType {
  const { foundationBottom, foundationTop, floorBottom, floorTop, ceilingBottom, ceilingTop, roofBottom, roofTop } =
    datums;

  // from bottom up until ROOM
  if (cutPlane < foundationBottom) {
    return WallSideCutType.OUT_OF_BOUNDS;
  } else if (cutPlane < foundationTop) {
    return WallSideCutType.FOUNDATION;
  } else if (cutPlane < floorBottom) {
    return WallSideCutType.LOWER;
  } else if (cutPlane < floorTop) {
    return WallSideCutType.FLOOR;
  }

  // from top down until ROOM
  if (cutPlane > roofTop) {
    return WallSideCutType.OUT_OF_BOUNDS;
  } else if (cutPlane > roofBottom) {
    return WallSideCutType.ROOF;
  } else if (cutPlane > ceilingTop) {
    return WallSideCutType.UPPER;
  } else if (cutPlane > ceilingBottom) {
    return WallSideCutType.CEILING;
  }

  // default to room
  return WallSideCutType.ROOM;
}

/**
 * @deprecated should use a domain based approach since these values can overlap
 *
 * @param wallDescriptor - The wall descriptor to determine the cut type for.
 * @param wallSection - The wall section to determine the cut type for.
 * @param leftSide - Whether the cut is on the left side of the wall.
 * @returns The cut type for the given wall section.
 */
export function wallSectionSideCutType(wallDescriptor: WallDescriptor, wallSection: WallSection, leftSide: boolean) {
  const wallSideDatums = leftSide ? wallDescriptor.leftSide.wallSideDatums : wallDescriptor.rightSide.wallSideDatums;
  const wallCutPlane = levelInchesNew((wallSection.bottomOffset + wallSection.topOffset) / 2);
  return wallSideCutType(wallSideDatums, wallCutPlane);
}

/**
 * Detects whether a room on one side of a wall can have a facade applied. This does not mean the whole wall cannot
 * have a facade applied, but simply that the section of wall represented by the room topOfFloor to room bottomOfCeiling
 * cannot have a facade.
 */
export function wallRoomCanApplyFacade(wallDescriptor: WallDescriptor, leftSide: boolean) {
  const wallRoom = leftSide ? wallDescriptor.leftSide.wallRoom : wallDescriptor.rightSide.wallRoom;

  // no facades on interior rooms
  if (isInteriorRoomEnclosure[wallRoom.roomEnclosure]) {
    return false;
  }

  // always facades on rooms with no room walls (decks, porches etc...)
  if (!isWalledRoomEnclosure[wallRoom.roomEnclosure]) {
    return true;
  }

  // exterior rooms only apply facades if they have a flag enabled (Portico). This is
  // important because exterior rooms will always generate exterior cladding from the wall
  // assembly, but we don't want to add facades to that assembly
  return !!wallRoom.room?.attributes.hasInteriorWallFacades;
}

/**
 * Detects whether a specific wall section can have a facade applied on a given side. This checks whether the wall
 * assembly should accept facade layers. The goal is to prevent facades being applied to sections of the wall that will
 * have facades applied above and below the room domain of that wall side.
 */
export function wallSectionSideCanApplyFacade(
  wallDescriptor: WallDescriptor,
  wallSection: WallSection,
  isLeftSide: boolean,
) {
  const wallCut = wallSectionSideCutType(wallDescriptor, wallSection, isLeftSide);
  const wallSectionSide = isLeftSide ? wallSection.leftSide : wallSection.rightSide;
  const wallSectionCanApplyFacadeOnSide = wallSectionSide.cladding === WallCladdingType.EXTERIOR;

  // sections that are not room intersections only depend on the cladding type
  // on that side of the wall
  if (wallCut !== WallSideCutType.ROOM) {
    return wallSectionCanApplyFacadeOnSide;
  }

  // room intersections need additional checks
  return wallSectionCanApplyFacadeOnSide && wallRoomCanApplyFacade(wallDescriptor, isLeftSide);
}
